/**
 * Issue #92 — Zod wire schema for the `celebrations` / `achievement_unlocked`
 * WS verb.
 *
 * Single source of truth for the one frame that verb carries. Both apps/api
 * (construction) and apps/web (receipt) import from here, so the field names
 * cannot drift across the workspace boundary — the MED-8 schema-drift guard,
 * same shape as ../chores/messages.ts and ../shopping/messages.ts.
 *
 * This is the FOURTH occurrence of the same defect class, and the first on a
 * topic other than `chores`:
 *   - issue #79: the approval frame shipped only the approver's id, so the L3
 *     burst (which gates on the EARNER) never fired.
 *   - issue #83: the claim frame shipped `by`, the client read `claimed_by`,
 *     and the whole L1 tier was dead.
 *   - issue #86: built ../chores/messages.ts so those two could not recur —
 *     but scoped itself to the `chores` topic.
 *   - issue #92 (this one): the server broadcast `{key, unlockedAt}` while the
 *     hook gated on `payload.memberId !== viewerId`. `undefined !== viewerId`
 *     is always true, so EVERY L4 takeover early-returned. Dead ~4.5 months.
 * All four shipped with both suites green, because each side asserted against
 * a fixture it wrote itself. ACHIEVEMENT_UNLOCKED_FRAME at the bottom of this
 * file is the fixture both sides now assert against instead.
 *
 * The constraint numbers cited below are the six in ../chores/messages.ts, and
 * that file holds their canonical text — read them there. They are referenced
 * BY NUMBER from 19 source comments across the repo, so they are never
 * renumbered. What follows recaps only the three that bind this verb, in one
 * line each, and then spends its length on what is actually different here.
 * If a recap and the canonical text ever disagree, the canonical text wins.
 *
 * What is specific to this verb:
 *
 *  - No union, because the verb has exactly one member. The Out/In split is
 *    kept anyway: that asymmetry (constraint 4) is what buys partial-deploy
 *    survivability, and it is worth nothing at all if it is only applied to
 *    verbs that happen to have more than one shape.
 *
 *  - Every field is REQUIRED on construction (constraint 3).
 *    `exactOptionalPropertyTypes: true` (tsconfig.base.json) means a
 *    misspelled OPTIONAL field is silently DROPPED rather than rejected —
 *    which is exactly the failure mode being fixed here: a `memberId` that is
 *    merely absent produced `undefined !== viewerId`, a gate that can only
 *    ever be true, and no error anywhere.
 *
 *  - Strict on construction, LOOSE on receipt (constraint 4).
 *    `AchievementUnlockedFrameOut` is `.strict()` and is what the server's
 *    `achievementUnlockedFrame()` constructor parses through: an extra or
 *    misspelled key throws at the broadcast site, where it is cheap to notice.
 *    `AchievementUnlockedFrame` is the same object without `.strict()`, and is
 *    what the client `safeParse`s — because the day the server adds a field, a
 *    strict client parse would make every Workbox-cached old PWA reject the
 *    whole frame and the tier would die again.
 *
 *  - `unlockedAt` is loose-OPTIONAL on receipt, and it is the only field that
 *    is. It is the one field on this verb that NO client reads: `L4Takeover`
 *    takes `achievementKey` / `viewport` / `reducedMotion` and nothing else,
 *    and the hook only forwards `unlockedAt` onto a `CelebrationEvent` field
 *    that no renderer consumes. Exactly the `claimExpiresAt` / `approved_by`
 *    case in ../chores/messages.ts: requiring a field the client has no use
 *    for would turn a future server-side rename of it into a dead tier — the
 *    very failure this module exists to stop, through the back door.
 *    `memberId` and `key` stay required on receipt, because a frame missing
 *    either genuinely cannot drive a celebration (no one to gate on, nothing
 *    to render) and dropping it is the correct outcome.
 *
 *    The trade-off, stated plainly: `.partial()` is ABSENT-tolerant but not
 *    MALFORMED-tolerant. `unlockedAt: 'now'` still fails `z.iso.datetime()`
 *    and takes the whole parse down with it. That is precisely how
 *    `claimExpiresAt` behaves today; the precedent is mirrored deliberately
 *    rather than invent a looser third variant for one verb.
 *
 *  - `.strip()` before `.partial()` is NOT redundant and must not be tidied
 *    away: `.partial()` inherits the strictness of the schema it is called on,
 *    so `AchievementUnlockedFrameOut.partial({unlockedAt:true})` alone would
 *    still REJECT unknown keys, quietly undoing the loose-receipt guarantee.
 *    The "strips an unknown key on receipt" case in messages.test.ts is what
 *    keeps that from regressing.
 *
 * The client gate is KEPT, deliberately, even though the transport already
 * makes it redundant: `hub.broadcastToMember` only ever delivers this frame to
 * the unlocking member's own sockets, so `memberId === viewerId` holds by
 * construction today. It stays because the L3 gate uses the identical
 * `claimed_by === viewerId` pattern, and a future refactor that swaps
 * `broadcastToMember` for `broadcast` would otherwise silently leak someone
 * else's achievement onto every screen in the house. Measured cost: +50 bytes
 * against the 256-byte INV-WS-1 budget.
 */
import { z } from 'zod';

/** Constraint 5: equality-compared ids, not parsed ones. */
const IdSchema = z.string().min(1);

/**
 * Construction side (constraint 4). `memberId` is the member who unlocked —
 * both the routing key and what the client gates on. `key` is the registry
 * achievement key the L4 modal renders.
 */
export const AchievementUnlockedFrameOut = z
  .object({
    memberId: IdSchema,
    key: IdSchema,
    unlockedAt: z.iso.datetime(),
  })
  .strict();

/**
 * Receipt side (constraint 4): the same object, non-strict — unknown keys are
 * stripped instead of rejected, so a newer server that adds a field does not
 * make an old Workbox-cached client throw the whole frame away. This is what
 * apps/web `safeParse`s.
 *
 * Note this costs nothing on the construction side. The compile-time lock
 * lives in `achievementUnlockedFrame()`'s parameter type, which is inferred
 * from `AchievementUnlockedFrameOut` above — where every field is required.
 */
export const AchievementUnlockedFrame = AchievementUnlockedFrameOut.strip().partial({
  unlockedAt: true,
});

/** Same inferred type either way — only the unknown-key policy differs. */
export type AchievementUnlockedFrame = z.infer<typeof AchievementUnlockedFrameOut>;

/**
 * The typed constructor every server broadcast on this verb goes through.
 * The parameter type catches a misspelling at COMPILE time; the strict parse
 * catches anything that reached runtime anyway (a cast, a spread, a widened
 * `unknown`). Both layers matter — constraint 6 in ../chores/messages.ts
 * explains why the hub itself cannot be the guard.
 */
export function achievementUnlockedFrame(
  f: AchievementUnlockedFrame,
): AchievementUnlockedFrame {
  return AchievementUnlockedFrameOut.parse(f);
}

// ── Canonical fixture ───────────────────────────────────────────────────────
// Exported so apps/api, apps/web and this package's own suite all assert
// against the SAME object rather than against literals each of them wrote.
// That three-way lock is the actual fix for #79 / #83 / #86 / #92.
//
// The id is a real 36-char UUIDv7 on purpose: the INV-WS-1 byte guard that
// consumes this fixture has to measure what production actually sends, not a
// frame ~30 bytes smaller built from 'm_kid'.
//
// It is deliberately a DIFFERENT member from ../chores/messages.ts's
// CHORE_FRAME_KID_ID (…1e06) — this one is …1e08. Reusing the kid's id would
// cost nothing on the wire but would make every D-10 gate in the celebration
// suites pass by coincidence: a test that overrides `memberId` to its own
// viewer, or forgets to, behaves identically when the two constants happen to
// be equal, so it can no longer tell a working gate from a deleted one. The
// tiers are gated on WHOSE event it is, which is exactly the property that
// needs two distinct people to be observable.

export const ACHIEVEMENT_FRAME_MEMBER_ID = '0199f3a2-7c4d-7e1b-9a55-3f2b8c6d1e08';

export const ACHIEVEMENT_UNLOCKED_FRAME = {
  memberId: ACHIEVEMENT_FRAME_MEMBER_ID,
  key: 'first_chore',
  unlockedAt: '2026-04-15T10:00:00.000Z',
} as const satisfies AchievementUnlockedFrame;
